import React, { useEffect, useState } from 'react';
import { useStore, phaseStatus, countDone } from '../store';
import { Phase, FactoryConfig } from '../types';
import { projectUsage, UsageTotals, getFactoryConfig } from '../lib/api';
import { Station, StatusDot, StatusLoz, I } from './Shared';

export function Sidebar() {
  const project = useStore((s) => s.project);
  const activeIdx = useStore((s) => s.activePhaseIdx);
  const expanded = useStore((s) => s.expandedPhases);
  const setActivePhase = useStore((s) => s.setActivePhase);
  const toggleExpand = useStore((s) => s.togglePhaseExpand);
  const showArtifact = useStore((s) => s.showArtifact);

  const tokenPills = useStore((s) => s.tokenPills);
  const assemblyLines = useStore((s) => s.assemblyLines);
  const [usage, setUsage] = useState<UsageTotals | null>(null);
  const [cfg, setCfg] = useState<FactoryConfig | null>(null);

  useEffect(() => {
    getFactoryConfig().then(setCfg).catch(() => setCfg(null));
  }, []);

  useEffect(() => {
    if (!project || !project.id.startsWith('p_')) {
      setUsage(null);
      return;
    }
    let cancelled = false;
    const refresh = (): void => {
      projectUsage(project.id)
        .then((u) => !cancelled && setUsage(u))
        .catch(() => undefined);
    };
    refresh();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, tokenPills]);

  if (!project) return null;

  const assemblyLineName = assemblyLines.find((b) => b.id === project.assemblyLineId)?.name
    ?? project.assemblyLineId ?? cfg?.brandLabel ?? 'App Factory';

  const totalSteps = project.phases.reduce((n, p) => n + p.steps.length, 0);
  const doneSteps = project.phases.reduce((n, p) => n + p.steps.filter((s) => s.status === 'done').length, 0);
  const hasRunning = project.phases.some((p) => p.steps.some((s) => s.status === 'in-progress'));

  return (
    <aside style={{ borderRight: '1px solid var(--line)', background: 'var(--bg-sunken)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ padding: '16px 14px 8px' }}>
        <div className="t-micro" style={{ marginBottom: 4 }}>{assemblyLineName}{project.assemblyLineVersion ? ` v${project.assemblyLineVersion}` : ''}</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span className="t-num" style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.02em' }}>{doneSteps}</span>
          <span className="muted t-small">/ {totalSteps} steps</span>
          <span style={{ flex: 1 }} />
          {hasRunning && <StatusLoz status="in-progress">Running</StatusLoz>}
        </div>
      </div>

      <div className="scroll" style={{ flex: 1, padding: '8px 8px' }}>
        <div style={{ padding: '4px 0' }}>
          {project.phases.map((phase, i) => (
            <PhaseBlock
              key={phase.key}
              phase={phase}
              index={i}
              isActive={i === activeIdx}
              isExpanded={expanded.has(phase.key)}
              onActivate={() => {
                setActivePhase(i);
                toggleExpand(phase.key);
              }}
              onStepClick={(stepKey, hasArtifacts) => {
                setActivePhase(i);
                if (hasArtifacts) showArtifact(stepKey);
              }}
            />
          ))}
        </div>
      </div>

      {/* pipeline context — hidden in demo mode */}
      {project.id.startsWith('p_') && <div style={{ borderTop: '1px solid var(--line)', padding: '14px 14px', background: 'var(--surface-2)' }}>
        <div className="t-micro" style={{ marginBottom: 10 }}>Pipeline context</div>
        <dl style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', gap: '6px 14px', margin: 0, fontSize: 12 }}>
          <dt className="muted">App name</dt>
          <dd style={{ margin: 0, fontWeight: 500 }}>{project.appName}</dd>
          {project.assemblyLineId && (
            <>
              <dt className="muted">Assembly Line</dt>
              <dd style={{ margin: 0 }}>{project.assemblyLineId}</dd>
            </>
          )}
          {project.assemblyLineVersion != null && (
            <>
              <dt className="muted">Assembly Line ver.</dt>
              <dd style={{ margin: 0 }}>v{project.assemblyLineVersion}</dd>
            </>
          )}
          {Object.entries(project.metadata ?? {}).map(([k, v]) => (
            <React.Fragment key={k}>
              <dt className="muted">{titleCase(k)}</dt>
              <dd style={{ margin: 0, overflowWrap: 'anywhere' }}>{v}</dd>
            </React.Fragment>
          ))}
          {project.target && (
            <>
              <dt className="muted">Target</dt>
              <dd style={{ margin: 0 }}>{project.target}</dd>
            </>
          )}
          {project.buildPath && (
            <>
              <dt className="muted">Build path</dt>
              <dd className="mono" style={{ margin: 0, overflowWrap: 'anywhere' }}>{project.buildPath}</dd>
            </>
          )}
          {usage && (
            <>
              <dt className="muted">Tokens</dt>
              <dd className="mono" style={{ margin: 0 }}>{formatTokens(usage.inputTokens + usage.outputTokens)}</dd>
              <dt className="muted">Cost</dt>
              <dd className="mono" style={{ margin: 0 }}>${usage.costUsd.toFixed(3)}</dd>
            </>
          )}
        </dl>
      </div>}
    </aside>
  );
}

function PhaseBlock({
  phase,
  index,
  isActive,
  isExpanded,
  onActivate,
  onStepClick,
}: {
  phase: Phase;
  index: number;
  isActive: boolean;
  isExpanded: boolean;
  onActivate: () => void;
  onStepClick: (stepKey: string, hasArtifacts: boolean) => void;
}) {
  const status = phaseStatus(phase);
  const doneCount = phase.steps.filter((s) => s.status === 'done').length;
  const allDone = doneCount === phase.steps.length;
  const hasRunning = phase.steps.some((s) => s.status === 'in-progress');
  const outdatedCount = phase.steps.filter((s) => !!s.outdatedReason).length;
  const hasOutdated = outdatedCount > 0;
  const phaseTimeMs = phase.steps.reduce((sum, s) => sum + (s.totalTimeMs ?? 0), 0);

  return (
    <div style={{ marginBottom: 2 }}>
      <button
        onClick={onActivate}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          width: '100%',
          padding: '8px 10px',
          border: 0,
          background: isActive ? 'var(--surface-2)' : 'transparent',
          borderRadius: 6,
          cursor: 'pointer',
          color: 'var(--ink)',
          fontFamily: 'inherit',
          textAlign: 'left',
          position: 'relative',
        }}
      >
        {isActive && (
          <span style={{ position: 'absolute', left: -10, top: 6, bottom: 6, width: 2, background: 'var(--accent-2)', borderRadius: 1 }} />
        )}
        <Station
          n={index + 1}
          size="sm"
          done={allDone}
          stale={hasOutdated && !hasRunning}
          ghost={!allDone && !hasRunning && doneCount === 0 && !hasOutdated}
        />
        <span style={{ fontWeight: 600, fontSize: 13, flex: 1 }}>{phase.label}</span>
        {hasOutdated && (
          <span
            className="loz loz--stale"
            style={{ height: 18, padding: '0 6px', fontSize: 10 }}
            title={`${outdatedCount} step${outdatedCount === 1 ? '' : 's'} need re-run`}
          >
            {outdatedCount} outdated
          </span>
        )}
        <span className="t-num" style={{ fontSize: 11, color: 'var(--ink-muted)' }}>
          {doneCount}/{phase.steps.length}
          {phaseTimeMs > 0 && <span> · {formatDuration(phaseTimeMs)}</span>}
        </span>
        <span style={{ color: 'var(--ink-faint)', display: 'flex' }}>
          {isExpanded ? I.chevD : I.chevR}
        </span>
      </button>
      {isExpanded && (
        <div style={{ paddingLeft: 32, paddingTop: 2, paddingBottom: 6, position: 'relative' }}>
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: 22, top: 0, bottom: 4, width: 1,
              background: 'var(--line)',
            }}
          />
          {phase.steps.map((step) => (
            <button
              key={step.key}
              onClick={(e) => {
                e.stopPropagation();
                onStepClick(step.key, step.artifacts.length > 0);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 9,
                width: '100%',
                padding: '5px 8px',
                border: 0,
                background: 'transparent',
                borderRadius: 4,
                cursor: 'pointer',
                color: 'var(--ink-2)',
                fontFamily: 'inherit',
                textAlign: 'left',
                fontSize: 12.5,
              }}
            >
              <StatusDot status={step.outdatedReason ? 'stale' : step.status} />
              <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{step.label}</span>
              {step.outdatedReason && (
                <span
                  className="t-micro"
                  style={{ color: 'var(--warn)', fontWeight: 600 }}
                  title={step.outdatedReason}
                >
                  outdated
                </span>
              )}
              {step.pinnedAtVersion != null && <span style={{ color: 'var(--warn)' }} title="Pinned">{I.pin}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function titleCase(s: string): string {
  if (!s) return s;
  return s[0].toUpperCase() + s.slice(1);
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'k';
  return String(n);
}

function formatDuration(ms: number): string {
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  return remM > 0 ? `${h}h ${remM}m` : `${h}h`;
}
